import {
  ApiError, Badge, Button, Card, DataTable, EmptyState, ErrorState, Field, Input, Row, Select, Stack,
  useApi, useI18n, type AcademicYearResponse, type Column, type ClassResponse, type CreateClassRequest,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

/** The largest page the API serves, so that every year a nursery has kept is in the picker. */
const YEARS_PAGE_SIZE = 100;

/** The tenant's academic years, latest first, for the filter and the class form's picker (Q34). */
function useAcademicYears() {
  const api = useApi();
  return useQuery({
    queryKey: ['academic-years'],
    queryFn: () => api.academicYears.list({ pageSize: YEARS_PAGE_SIZE }),
  });
}

/**
 * Classes and their capacity (FR-ADM-04), each in an academic year (Q34): created, corrected and archived here
 * (FR-ADM-11, FR-ADM-13). Archiving is refused while children are still in the class, in the API's own words.
 */
export function Classes() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t, formatNumber } = useI18n();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ClassResponse | null>(null);
  const [archiving, setArchiving] = useState<number | null>(null);
  const [yearId, setYearId] = useState<number | ''>('');

  const years = useAcademicYears();
  const classes = useQuery({
    queryKey: ['classes', { academicYearId: yearId }],
    queryFn: () => api.classes.list(yearId === '' ? {} : { academicYearId: yearId }),
  });

  const archive = useMutation({
    mutationFn: (id: number) => api.classes.archive(id),
    onSuccess: async () => {
      setArchiving(null);
      await queryClient.invalidateQueries({ queryKey: ['classes'] });
    },
  });

  const columns: Column<ClassResponse>[] = [
    {
      key: 'name',
      header: t('classes.column.name'),
      render: (klass) => (
        <Row>
          <strong>{klass.name}</strong>
          {klass.status === 'Archived' && <Badge tone="neutral">{t('classes.archived')}</Badge>}
        </Row>
      ),
    },
    {
      key: 'ages',
      header: t('classes.column.ages'),
      render: (klass) => klass.ageMinMonths != null && klass.ageMaxMonths != null
        ? t('classes.ageRange', {
            min: formatNumber(klass.ageMinMonths), max: formatNumber(klass.ageMaxMonths),
          })
        : '—',
    },
    {
      key: 'occupancy',
      header: t('classes.column.enrolled'),
      numeric: true,
      render: (klass) => (
        <Row>
          <span>{t('classes.occupancy', {
            enrolled: formatNumber(klass.activeChildren), capacity: formatNumber(klass.capacity),
          })}</span>
          {/* A class at capacity is the thing an owner is looking for when they open this page. */}
          {klass.activeChildren >= klass.capacity && <Badge tone="warning">{t('classes.full')}</Badge>}
        </Row>
      ),
    },
    { key: 'room', header: t('classes.column.room'), render: (klass) => klass.room ?? '—' },
    { key: 'year', header: t('classes.column.year'), render: (klass) => klass.academicYear },
    {
      key: 'actions',
      header: '',
      render: (klass) => klass.status === 'Archived' ? null : (
        <Row>
          <Button variant="ghost" aria-label={t('classes.editOf', { name: klass.name })}
            onClick={() => { setCreating(false); setEditing(klass); }}>
            {t('action.edit')}
          </Button>
          {archiving === klass.id ? (
            <>
              <Button variant="danger" busy={archive.isPending} onClick={() => archive.mutate(klass.id)}>
                {t('classes.archive.confirm')}
              </Button>
              <Button variant="ghost" onClick={() => setArchiving(null)}>{t('action.cancel')}</Button>
            </>
          ) : (
            <Button variant="ghost" aria-label={t('classes.archiveOf', { name: klass.name })}
              onClick={() => { archive.reset(); setArchiving(klass.id); }}>
              {t('classes.archive')}
            </Button>
          )}
        </Row>
      ),
    },
  ];

  return (
    <>
      <div className="dg-page-header">
        <div className="dg-page-header__row">
          <h1>{t('classes.title')}</h1>
          <Button onClick={() => { setEditing(null); setCreating((current) => !current); }} icon="＋">{t('classes.new')}</Button>
        </div>
        <p className="dg-page-header__lede">{t('classes.lede')}</p>
      </div>

      {creating && <ClassForm years={years.data?.items ?? []} onDone={() => setCreating(false)} />}
      {editing && <ClassForm key={editing.id} years={years.data?.items ?? []} klass={editing} onDone={() => setEditing(null)} />}
      {archive.isError && <ErrorState error={archive.error} />}

      <Row>
        <Select
          aria-label={t('classes.filter.year')}
          value={yearId}
          onChange={(event) => setYearId(event.target.value === '' ? '' : Number(event.target.value))}
        >
          <option value="">{t('classes.filter.allYears')}</option>
          {(years.data?.items ?? []).map((year) => <option key={year.id} value={year.id}>{year.name}</option>)}
        </Select>
      </Row>

      <DataTable
        caption={t('classes.caption')}
        columns={columns}
        rows={classes.data?.items}
        rowKey={(klass) => klass.id}
        isLoading={classes.isPending}
        error={classes.error}
        onRetry={() => void classes.refetch()}
        empty={<EmptyState icon="🧩" title={t('classes.empty')} />}
      />
    </>
  );
}

/**
 * Creates a class, or corrects one (FR-ADM-11). A class keeps its year when it is corrected: moving rooms between
 * years is what the year's close does (Q36), not an edit.
 */
function ClassForm({ years, klass, onDone }: {
  years: AcademicYearResponse[]; klass?: ClassResponse; onDone: () => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  // Null sends no year, and the API places a new class in the current one.
  const [form, setForm] = useState<CreateClassRequest>(klass
    ? {
        name: klass.name, ageMinMonths: klass.ageMinMonths, ageMaxMonths: klass.ageMaxMonths, capacity: klass.capacity,
        room: klass.room ?? '', academicYearId: klass.academicYearId, diaperLogEnabled: klass.diaperLogEnabled,
      }
    : {
        name: '', ageMinMonths: null, ageMaxMonths: null, capacity: 20,
        room: '', academicYearId: null, diaperLogEnabled: false,
      });

  const save = useMutation({
    mutationFn: (body: CreateClassRequest) => (klass ? api.classes.update(klass.id, body) : api.classes.create(body)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classes'] });
      onDone();
    },
  });

  const set = <K extends keyof CreateClassRequest>(key: K, value: CreateClassRequest[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate({
      ...form,
      name: form.name.trim(),
      room: form.room?.trim() || null,
    });
  }

  const fieldErrors = save.error instanceof ApiError ? save.error.fieldErrors : {};

  return (
    <Card title={klass ? t('classes.editOf', { name: klass.name }) : t('classes.new')}>
      <form onSubmit={submit} noValidate>
        <Stack>
          {save.isError && <p className="dg-field__error" role="alert">{save.error.message}</p>}

          <Field label={t('classes.new.name')} error={fieldErrors.name} required>
            {(props) => <Input {...props} value={form.name} onChange={(e) => set('name', e.target.value)} autoFocus />}
          </Field>

          <Row>
            <Field label={t('classes.new.ageMin')}>
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  value={form.ageMinMonths ?? ''}
                  onChange={(e) => set('ageMinMonths', e.target.value ? Number(e.target.value) : null)}
                />
              )}
            </Field>
            <Field label={t('classes.new.ageMax')}>
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  value={form.ageMaxMonths ?? ''}
                  onChange={(e) => set('ageMaxMonths', e.target.value ? Number(e.target.value) : null)}
                />
              )}
            </Field>
            <Field label={t('classes.new.capacity')} error={fieldErrors.capacity} required>
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={1}
                  value={form.capacity}
                  onChange={(e) => set('capacity', Number(e.target.value))}
                />
              )}
            </Field>
          </Row>

          <Row>
            <Field label={t('classes.new.room')}>
              {(props) => <Input {...props} value={form.room ?? ''} onChange={(e) => set('room', e.target.value)} />}
            </Field>
            {!klass && (
              <Field label={t('classes.new.year')}>
                {(props) => (
                  <Select
                    {...props}
                    value={form.academicYearId ?? ''}
                    onChange={(e) => set('academicYearId', e.target.value === '' ? null : Number(e.target.value))}
                  >
                    {/* A closed year takes no new class; the API would refuse it. */}
                    {years.filter((year) => year.status !== 'Closed').map((year) => (
                      <option key={year.id} value={year.status === 'Current' ? '' : year.id}>
                        {year.status === 'Current' ? t('classes.new.year.current', { name: year.name }) : year.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}
          </Row>

          {/* The nappy action only appears for teachers in classes where this is on, which is why it is a
              property of the class rather than a tenant-wide setting (FR-STF-03). */}
          <label className="dg-row">
            <input
              type="checkbox"
              checked={form.diaperLogEnabled}
              onChange={(e) => set('diaperLogEnabled', e.target.checked)}
              style={{ inlineSize: 20, blockSize: 20 }}
            />
            <span className="dg-stack dg-stack--tight">
              <span>{t('classes.new.diaperLog')}</span>
              <span className="dg-field__hint">{t('classes.new.diaperLog.hint')}</span>
            </span>
          </label>

          <Row>
            <Button type="submit" busy={save.isPending} busyLabel={t('action.saving')}>
              {klass ? t('action.save') : t('classes.new.submit')}
            </Button>
            <Button variant="ghost" onClick={onDone}>{t('action.cancel')}</Button>
          </Row>
        </Stack>
      </form>
    </Card>
  );
}
